import App from "./App";
import { IntlProvider } from "react-intl";
import { Provider } from "react-redux";
import React from "react";
import ReactDOM from "react-dom/client";
import { icons } from "./assets/icons";
import store from "./store";
import { ThemeProvider } from "@mui/material";
import { theme } from "./theme";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PostHogProvider } from "@posthog/react";
import { setPostHogUser } from "./config/posthog";
// Check if vite is running in development mode
if (import.meta.env.DEV) {
  window.global = window;

  // Dev safeguard: a service worker left over from a past *production* build on
  // this origin (registered by vite-plugin-pwa) keeps serving a stale precached
  // bundle in dev — it survives reloads and browser restarts and masks code
  // changes. Dev serves no SW (devOptions.enabled=false), so proactively
  // unregister any controlling SW + drop its caches, then reload once onto the
  // live dev bundle.
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker
      .getRegistrations()
      .then((regs) => {
        if (!regs.length) return null;
        return Promise.all(regs.map((r) => r.unregister()))
          .then(() =>
            "caches" in window
              ? caches
                  .keys()
                  .then((keys) =>
                    Promise.all(keys.map((k) => caches.delete(k))),
                  )
              : null,
          )
          .then(() => window.location.reload());
      })
      // Best-effort: a rejection here (e.g. storage permissions) must not
      // surface as an unhandled rejection masking real dev errors.
      .catch((err) => console.warn("Dev service-worker cleanup failed:", err));
  }
}

React.icons = icons;
const root = ReactDOM.createRoot(document.getElementById("root"));

// replace console.* for disable log on production
if (import.meta.env.NODE_ENV === "production") {
  // currently only console log is disabled, other commented out are enabled.
  console.log = () => {};
  // console.warn = () => {};
  // console.error = () => {};
  // console.debug = () => {};
}
// overriding the console.error to ignore the warning about defaultProps
const originalError = console.error;

console.error = function (message, ...args) {
  // ignore the warning about defaultProps due to outdated libraries not supported by React 17
  // will only trigger for errors that have this specific message and are of type string
  if (
    typeof message === "string" &&
    message.includes(
      "Support for defaultProps will be removed from function components in a future major release. Use JavaScript default parameters instead.",
    )
  ) {
    return;
  }
  // Call the original console.error for all other errors
  originalError.apply(console, [message, ...args]);
};

const POSTHOG_PROJECT_TOKEN = window.ENV?.POSTHOG_PROJECT_TOKEN;
const POSTHOG_HOST = window.ENV?.POSTHOG_HOST;
const NO_POSTHOG_ACCESS = window.ENV?.NO_POSTHOG_ACCESS;
const posthogEnabled =
  !NO_POSTHOG_ACCESS && !!POSTHOG_PROJECT_TOKEN && !!POSTHOG_HOST;

const posthogOptions = {
  api_host: POSTHOG_HOST,
  capture_pageview: "history_change",
  loaded: (posthog) => {
    posthog.register({
      customer_env: window.ENV?.CUSTOMER_ENV || "",
      app_version: window.ENV?.NUCLEARN_VERSION || "",
      environment: window.ENV?.ENVIRONMENT || import.meta.env.MODE,
      deployment_type: window.ENV?.DEPLOYMENT_TYPE || "on-prem",
    });
    setPostHogUser();
    if (import.meta.env.DEV) {
      window.posthog = posthog;
    }
  },
};

const appTree = (
  <ThemeProvider theme={theme}>
    <Provider store={store}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <IntlProvider locale="en">
          <App />
        </IntlProvider>
      </LocalizationProvider>
    </Provider>
  </ThemeProvider>
);

root.render(
  posthogEnabled ? (
    <PostHogProvider apiKey={POSTHOG_PROJECT_TOKEN} options={posthogOptions}>
      {appTree}
    </PostHogProvider>
  ) : (
    appTree
  ),
);

// PWA Service Worker - handled by vite-plugin-pwa
// The service worker is automatically registered in production builds
// See vite.config.ts for PWA configuration
